import React, { useState, useEffect } from 'react';
import { Loader, CreditCard, ArrowLeft, CheckCircle, RefreshCw } from 'lucide-react';
import PagoMultiple from './Pago_multiple';
import PagoMultipleTransferencia from './pago_multiple_transferencia';
import { FaExchangeAlt } from 'react-icons/fa';
import { verificarHijos, obtenerMatriculaPorId } from '../../services/matriculaService';
import { obtenerPlanes } from '../../services/centroService';
import { verificarPagosAnterioresHistorial, obtenerTodosControlPagos } from '../../services/pagosServices';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ControladorPagoMultiple = () => {
    const [metodoPago, setMetodoPago] = useState('');
    const [mostrarSeleccionPlan, setMostrarSeleccionPlan] = useState(false);
    const [planes, setPlanes] = useState([]);
    const [planActual, setPlanActual] = useState(null);
    const [planSeleccionado, setPlanSeleccionado] = useState(null);
    const [tienePagosAnteriores, setTienePagosAnteriores] = useState(false);
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hijosSeleccionados, setHijosSeleccionados] = useState([]);
    const [transferenciaPendiente, setTransferenciaPendiente] = useState(false);
    const [pagoTarjetaPendiente, setPagoTarjetaPendiente] = useState(false);
    const [mostrarMensajeTransferencia, setMostrarMensajeTransferencia] = useState(false);

    const volverASeleccion = () => {
        setMetodoPago('');
        setMostrarSeleccionPlan(false);
    };

    // Verificar pagos pendientes cuando cambien los hijos seleccionados
    useEffect(() => {
        if (hijosSeleccionados.length > 0) {
            verificarPagosPendientes();
        } else {
            setTransferenciaPendiente(false);
            setPagoTarjetaPendiente(false);
        }
    }, [hijosSeleccionados]);

    const cargarPlanes = async () => {
        try {
            setLoadingPlanes(true);
            const response = await obtenerPlanes();
            if (response.success && response.data) {
                setPlanes(response.data);
            }
        } catch (error) {
            console.error('Error al cargar planes:', error);
            toast.error('Error al cargar los planes disponibles');
        } finally {
            setLoadingPlanes(false);
        }
    };

    const verificarPagosAnteriores = async (idMatricula) => {
        try {
            const response = await verificarPagosAnterioresHistorial(idMatricula);
            if (response.success) {
                return response.tienePagosAnteriores;
            }
            return false;
        } catch (error) {
            console.error('Error al verificar pagos anteriores:', error);
            return false;
        }
    };

    const verificarPagosPendientes = async () => {
        try {
            const response = await obtenerTodosControlPagos();
            if (response.success) {
                // Solo mostrar información sobre pagos pendientes, pero no bloquear
                const transferencias = response.data.filter(
                    pago => pago.tipo_pago === 'Transferencia' && 
                           pago.estado === 'Pendiente' &&
                           pago.estado_verificacion === 'Pendiente' && 
                           pago.imagen_comprobante &&
                           hijosSeleccionados.some(hijo => hijo.id_matricula === pago.id_matricula)
                );
                setTransferenciaPendiente(transferencias.length > 0);
                
                const pagosTarjeta = response.data.filter(
                    pago => pago.tipo_pago === 'Tarjeta' && 
                           pago.estado === 'Pendiente' &&
                           pago.estado_verificacion === 'Pendiente' &&
                           hijosSeleccionados.some(hijo => hijo.id_matricula === pago.id_matricula)
                );
                setPagoTarjetaPendiente(pagosTarjeta.length > 0);
            }
        } catch (err) {
            console.error('Error al verificar pagos pendientes:', err);
        }
    };

    const handleSeleccionarPlan = (plan, e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        setPlanSeleccionado(plan);
    };

    const confirmarCambioPlan = async (e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        if (planSeleccionado) {
            try {
                // Guardar el plan seleccionado en localStorage para persistencia
                localStorage.setItem('planSeleccionado', JSON.stringify(planSeleccionado));
                localStorage.setItem('planActual', JSON.stringify(planActual));
                localStorage.setItem('planSeleccionadoMultiple', JSON.stringify(planSeleccionado));
                
                // Notificar a los componentes de pago que se confirmó el plan
                // Esto permitirá que mantengan la selección del hijo y actualicen el monto
                const evento = new CustomEvent('planConfirmado', {
                    detail: {
                        plan: planSeleccionado,
                        planActual: planActual
                    }
                });
                window.dispatchEvent(evento);
                
                // Cambiar el estado después de que todo se haya procesado correctamente
                setMostrarSeleccionPlan(false);
            } catch (error) {
                console.error('Error al confirmar cambio de plan:', error);
                toast.error('Error al confirmar el plan seleccionado');
            }
        }
    };
    
    const cancelarSeleccionPlan = (e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        try {
            // Limpiar cualquier dato temporal en localStorage
            localStorage.removeItem('hijoSeleccionadoTemp');
            
            // Notificar a los componentes de pago que se canceló la selección
            const evento = new CustomEvent('planCancelado');
            window.dispatchEvent(evento);
            
            // Cambiar el estado después de que todo se haya procesado correctamente
            setMostrarSeleccionPlan(false);
        } catch (error) {
            console.error('Error al cancelar selección de plan:', error);
            toast.error('Error al cancelar la selección del plan');
        }
    };

    const procederAlPago = (metodo) => {
        setMetodoPago(metodo);
        setMostrarSeleccionPlan(false);
    };

    // Función para actualizar los hijos seleccionados
    const actualizarHijosSeleccionados = (hijos) => {
        setHijosSeleccionados(hijos);
    };

    // Función para manejar la selección de un niño específico
    const manejarSeleccionNino = async (hijo) => {
        try {
            // Obtener información de la matrícula del niño
            const response = await obtenerMatriculaPorId(hijo.id_child);
            const idMatricula = response.matricula.matricula.id_matricula;
            const matriculaData = response.matricula.matricula;
            
            // Verificar si tiene pagos anteriores
            const tienePagos = await verificarPagosAnteriores(idMatricula);
            
            if (tienePagos) {
                // Si tiene pagos anteriores, mostrar modal de selección de plan
                setTienePagosAnteriores(true);
                
                // Obtener y establecer el plan actual del niño
                if (matriculaData.id_plan && planes.length > 0) {
                    const planEncontrado = planes.find(p => p.id_plan === matriculaData.id_plan);
                    if (planEncontrado) {
                        setPlanActual(planEncontrado);
                        setPlanSeleccionado(planEncontrado);
                    }
                }
                
                setMostrarSeleccionPlan(true);
            } else {
                // Si no tiene pagos anteriores, ir directo a selección de método de pago
                setTienePagosAnteriores(false);
                setMostrarSeleccionPlan(false);
                // Aquí podrías agregar lógica adicional para proceder directamente al pago
            }
        } catch (error) {
            console.error('Error al manejar selección de niño:', error);
            toast.error('Error al verificar información del niño');
        }
    };

    // Función para renderizar la descripción con beneficios como viñetas
    const renderDescripcion = (descripcion) => {
        if (!descripcion) return null;
        
        const partes = descripcion.split(';');
        
        if (partes.length <= 1) {
            return <p>{descripcion}</p>;
        }
        
        const descripcionBase = partes[0].trim();
        const beneficios = partes.slice(1).filter(beneficio => beneficio.trim());
        
        return (
            <div>
                {descripcionBase && (
                    <p className="mb-2">{descripcionBase}</p>
                )}
                {beneficios.length > 0 && (
                    <div>
                        <p className="font-medium text-gray-700 mb-1">Incluye:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {beneficios.map((beneficio, index) => (
                                <li key={index} className="text-xs">
                                    {beneficio.trim()}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const verificarEstadoInicial = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                const userId = user?.id_usuario || user?.id;
                
                if (user && userId) {
                    const hijosResponse = await verificarHijos(userId);
                    const hijosActivos = hijosResponse.hijos?.filter(hijo => hijo.estado_matricula === 'Activa') || [];
                    setHijosSeleccionados(hijosActivos);
                    
                    // Solo cargar los planes disponibles, pero no mostrar el modal automáticamente
                    await cargarPlanes();
                }
            } catch (error) {
                console.error('Error al verificar estado inicial:', error);
            } finally {
                setIsLoading(false);
            }
        };
        
        verificarEstadoInicial();
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-2 sm:p-4">
                <Loader className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-600 mb-3 sm:mb-4" />
                <p className="text-sm sm:text-base text-gray-600">Verificando estado de pagos...</p>
            </div>
        );
    }

    // Modal de selección de plan - solo se muestra cuando se selecciona un niño con pagos anteriores
    if (mostrarSeleccionPlan) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="text-center space-y-2 mb-6">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Seleccione su Plan</h2>
                            <p className="text-sm sm:text-base text-gray-500">Renueve su plan actual o seleccione uno nuevo para todos los hijos</p>
                        </div>

                        {loadingPlanes ? (
                            <div className="flex justify-center items-center py-8">
                                <Loader className="w-8 h-8 animate-spin text-blue-600 mr-3" />
                                <span className="text-gray-600">Cargando planes...</span>
                            </div>
                        ) : (
                            <>
                                {planActual && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="w-5 h-5 text-blue-600" />
                                            <div>
                                                <h3 className="font-medium text-blue-900">Plan Actual: {planActual.nombre}</h3>
                                                <p className="text-sm text-blue-700">${planActual.monto} - {planActual.hora_inicio} a {planActual.hora_fin}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
                                    {planes.map((plan) => (
                                        <div 
                                            key={plan.id_plan} 
                                            className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                                                planSeleccionado?.id_plan === plan.id_plan 
                                                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                                            }`}
                                            onClick={(e) => handleSeleccionarPlan(plan, e)}
                                        >
                                            <div className="text-center">
                                                <div className="flex items-center justify-center gap-2 mb-2">
                                                    <h4 className="text-lg font-semibold text-gray-800">
                                                        {plan.nombre}
                                                    </h4>
                                                    {planActual?.id_plan === plan.id_plan && (
                                                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                            Actual
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-2xl font-bold text-blue-600 mb-3">
                                                    ${plan.monto}
                                                </p>
                                                {plan.descripcion && (
                                                    <div className="text-gray-600 text-sm mb-3">
                                                        {renderDescripcion(plan.descripcion)}
                                                    </div>
                                                )}
                                                <p className="text-sm text-gray-500 mb-4">
                                                    {plan.hora_inicio} - {plan.hora_fin}
                                                </p>
                                                <div
                                                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors text-center ${
                                                        planSeleccionado?.id_plan === plan.id_plan
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {planSeleccionado?.id_plan === plan.id_plan ? 'Seleccionado' : 'Seleccionar'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            cancelarSeleccionPlan(e);
                                        }}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    {planSeleccionado && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                confirmarCambioPlan(e);
                                            }}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            {planActual && planSeleccionado && planSeleccionado.id_plan !== planActual.id_plan ? 'Cambiar Plan' : 'Renovar Plan'}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!metodoPago) {
        return (
            <div className="min-h-screen flex justify-center p-2 sm:p-4">
                <div className="w-full max-w-md space-y-6 sm:space-y-8">
                    <div className="text-center space-y-2">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Seleccione método de pago</h2>
                        <p className="text-sm sm:text-base text-gray-500">Escoja cómo desea realizar su pago múltiple</p>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                        <button
                            onClick={() => procederAlPago('tarjeta')}
                            className="w-full p-3 sm:p-4 border rounded-lg sm:rounded-xl transition-all duration-200 bg-white/30 border-gray-200 hover:bg-blue-50"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                                    <div className="text-left">
                                        <h3 className="text-sm sm:text-base font-medium text-gray-900">Tarjeta de Crédito/Débito</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">Pago seguro con dLocal Go</p>
                                    </div>
                                </div>
                                {pagoTarjetaPendiente && (
                                    <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                                        Revisar
                                    </div>
                                )}
                            </div>
                        </button>

                        <button
                            onClick={() => procederAlPago('transferencia')}
                            className="w-full p-3 sm:p-4 border rounded-lg sm:rounded-xl transition-all duration-200 bg-white/30 border-gray-200 hover:bg-blue-50"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <FaExchangeAlt className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                                    <div className="text-left">
                                        <h3 className="text-sm sm:text-base font-medium text-gray-900">Transferencia Bancaria</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">Transferencia directa a nuestra cuenta</p>
                                    </div>
                                </div>
                                {transferenciaPendiente && (
                                    <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                                        Ver estado
                                    </div>
                                )}
                            </div>
                        </button>
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600">
                            Seleccione el método de pago para proceder con el pago múltiple
                        </p>
                    </div>

                    {(pagoTarjetaPendiente || transferenciaPendiente) && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        Información sobre pagos pendientes
                                    </h3>
                                    <p className="text-sm text-blue-700 mt-1">
                                        {transferenciaPendiente && pagoTarjetaPendiente 
                                            ? 'Tienes pagos pendientes tanto por transferencia como por tarjeta para algunos de tus hijos. Puedes continuar realizando pagos para otros hijos.'
                                            : transferenciaPendiente 
                                            ? 'Tienes una transferencia pendiente de verificación para algunos de tus hijos. Puedes continuar realizando pagos para otros hijos.'
                                            : 'Tienes un pago con tarjeta pendiente para algunos de tus hijos. Puedes continuar realizando pagos para otros hijos.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-4">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-4">
                    <button
                        onClick={volverASeleccion}
                        className="p-1.5 sm:p-2 flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-blue-600 transition-colors text-sm sm:text-base"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Volver</span>
                    </button>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {metodoPago === 'tarjeta' && 'Pago múltiple con tarjeta'}
                        {metodoPago === 'transferencia' && 'Pago múltiple por transferencia'}
                    </h2>
                </div>
            </div>
            {metodoPago === 'tarjeta' && <PagoMultiple onSeleccionarNino={manejarSeleccionNino} onActualizarHijos={actualizarHijosSeleccionados} />}
            {metodoPago === 'transferencia' && <PagoMultipleTransferencia onActualizarHijos={actualizarHijosSeleccionados} />}
        </div>
    );
};

export default ControladorPagoMultiple;